/** A list page: its canvas — the search bar, the buttons for the rows chosen, the table's columns — and its panels: the list's, a column's, a button's. */
export const list = {
  // ---- the canvas
  searchAndFilters: 'Search and filters',
  searchTitle: 'Where the search looks, the filters and the groupings: in the panel',
  addButton: 'Add a button',
  showsWhenChosen: 'Shows when rows are chosen',
  words: 'Words',
  delete: 'Delete',
  addColumn: 'Add a column',
  addColumnNote: (any: boolean): string =>
    any ? 'A list shows the fields the model has. New fields are made on the record’s page.' : 'Every field the model has that a column can show is a column already.',
  column: 'Column',
  moveLeft: 'Move left',
  moveRight: 'Move right',
  removeColumn: 'Remove the column',
  // ---- the list's panel
  rowsOnPage: 'Rows on a page',
  sortedBy: 'Sorted by',
  appOrder: 'As the app orders them',
  direction: 'Direction',
  ascending: 'A to Z, low to high, old to new',
  descending: 'Z to A, high to low, new to old',
  searchLooksIn: 'Search looks in',
  searchLooksInField: (label: string) => `Search looks in ${label}`,
  noneTicked: 'None ticked: it looks in every column.',
  filters: 'Filters',
  addFilter: 'Add a filter',
  newFilter: 'New filter',
  groupBy: 'Group by',
  addGrouping: 'Add a grouping',
  groupingNote: (any: boolean): string => (any ? 'People pick a grouping from the search bar; the list then shows a row for each value.' : 'No other field can group the list.'),
  removeGrouping: (label: string) => `Remove the grouping ${label}`,
  rowOpens: 'A row opens its record. The buttons for the rows chosen are added on the canvas, under the search bar.',
  // ---- the list drawn as a backlog: no box to choose a row, a filter bar of fields, rows under their parent
  choose: 'A box to choose each row',
  chooseHint: 'Without it a row cannot be chosen, and the list has no buttons for the rows chosen.',
  sortable: 'A header’s press sorts the list',
  sortableHint: 'Off for a ranked list, such as a backlog: it keeps its own order, and its headers are words alone.',
  exportRows: 'Export to Excel and CSV',
  exportHint: 'People take the rows they tick, or every one the search finds, away as a file: the columns as the list shows them.',
  bulkEdit: 'Edit many rows at once',
  bulkEditHint: 'With rows ticked, Edit sets one field to one value in each, after a question: any column that can be changed.',
  editInPlace: 'Change cells in place',
  editInPlaceHint: 'A press on a cell changes it in its field’s own box, Tab goes on to the next; each row is saved as it is left, and a row is added at the end: a price list, a week’s hours, a pipeline’s stages.',
  importRows: 'Import from Excel and CSV',
  importHint: 'People make records from a file of their own: its columns matched to the fields, each row checked by the page’s rules before anything is saved, and Undo after.',
  printRows: 'Print every row found',
  printHint: 'Print, in the list’s bar: every record the search finds on paper, under its groups with their sums, the totals under them, the filters said over them.',
  searchWay: 'Search',
  searchWays: { bar: 'The search bar', fields: 'A keyword and each field’s values' },
  filterFields: 'Fields with their values to tick',
  filterFieldNamed: (label: string) => `Offer ${label}’s values`,
  filterFieldsNote: 'A choice’s options, a link’s records, links’ records, yes and no, or the text and numbers the records hold; a decimal’s, an amount’s or a date’s range, From and To.',
  filterLabelOf: (label: string) => `${label}’s name on the filter bar`,
  filterLabelsNote: 'A shorter name on the bar where a field’s label is long: “Iteration” for Iteration Path. Empty: its label.',
  filterByRange: (label: string) => `${label} by its range, From and To`,
  filterAtLeastOf: (label: string) => `${label}’s least values, a press each`,
  filterAtLeastExample: '1, 2, 3, 4',
  filterRangesNote: 'A whole number’s values are listed unless it goes by its range. A number’s least values are a press each: “2+” bedrooms.',
  filterCounts: 'Say how many each filter finds',
  filterCountsHint: 'Beside each value of the filter bar, and each filter and saved search in the search bar’s menu: “Late 5”. Counted by the app’s server as the search changes.',
  searchToFilter: 'Search in plain words (AI)',
  searchToFilterHint: '“Villas in New Cairo under 15M” turned into the list’s own filters by your app’s AI (the data source’s searchToFilter), with Undo. Hidden where the source has none.',
  filterSheet: 'On a phone, the filters in a sheet',
  filterSheetHint: 'A Filters button opens every field at once; “Show 37 results” takes them together.',
  parentField: 'Rows under their parent, by',
  noParent: 'None: every row at the top',
  noParentField: 'No link here goes to the list’s own records.',
  // ---- the list's map
  map: 'Map',
  onAMap: 'Show the records on a map too',
  noPlace: 'A map needs where each record is: a JSON field holding a point, or two number fields for the latitude and the longitude.',
  where: 'Where each record is',
  onePoint: (label: string) => `${label}, one point`,
  latLng: 'A latitude and a longitude',
  latitude: 'Latitude',
  longitude: 'Longitude',
  pinShows: 'Each pin shows',
  nothing: 'Nothing',
  pinLook: 'Pins look like',
  pinLooks: { pin: 'A pin', label: 'Their words', icon: 'An icon', photo: 'A round photo', dot: 'A dot' },
  pinPhoto: 'Photo',
  pinIcon: 'Icon',
  iconFrom: 'Each record’s icon, from',
  turnedBy: 'Turned by, in degrees',
  cardTitle: 'Card title',
  cardPicture: 'Card picture',
  cardShows: 'Card shows',
  addToCard: 'Add a field to the card',
  takeOffCard: (label: string) => `Take ${label} off the card`,
  cardLink: 'Card links to the address in',
  cardLabels: 'Labels beside the card’s lines',
  cardLead: 'The first line large and bold',
  cardChoose: 'A box on the card to choose the record',
  homeCurrency: 'Home currency, unsaid on pins',
  opensAs: 'Opens as',
  opensAsChoices: { list: 'The list', map: 'The map', both: 'The list and the map' },
  bubbles: 'Pins that crowd become a bubble',
  mapTools: 'Tools under the zoom',
  mapToolNames: { nearest: 'Nearest first', satellite: 'Satellite', traffic: 'Traffic', fullscreen: 'Full screen', measure: 'Measure a distance' },
  heat: 'A heat map from afar',
  heatWeight: 'Each weighs',
  eachOne: 'One',
  areas: 'Areas by colour',
  noAreas: 'None',
  areaField: 'Each record’s area is in',
  noAreaField: 'No field can hold an area’s key: a text, a choice, a whole number or a link.',
  mapNote: 'The app chooses whose maps are shown. Pin colours by condition, a rent’s “/month” and card lines of several fields are written in the page’s JSON.',
  // How the map sits in its page: its height, the gestures it takes, the closest it zooms by itself.
  mapHeight: 'Map height',
  mapHeights: { window: 'As tall as the window allows', fill: 'As tall as the place it is put in', small: 'Small, 320 pixels', medium: 'Medium, 480 pixels', large: 'Large, 640 pixels' },
  pixels: (n: number) => `${n} pixels`,
  allGestures: 'Every gesture moves the map: one finger, and the wheel with no key held',
  fitZoom: 'Closest it zooms to show the records',
  // ---- a filter
  filterName: 'Filter name',
  filterField: 'Filter field',
  filterTest: 'Filter test',
  filterValue: 'Filter value',
  onWhenOpens: 'On when the list opens',
  remove: 'Remove',
  removeFilter: (label: string) => `Remove the filter ${label}`,
  byHand: 'Its conditions were written by hand: they stay as they are.',
  yes: 'Yes',
  no: 'No',
  /** How a filter compares a field: "Amount" "is more than" 100. */
  tests: {
    is: 'is',
    isNot: 'is not',
    filled: 'is filled in',
    empty: 'is empty',
    more: 'is more than',
    less: 'is less than',
    atLeast: 'is at least',
    atMost: 'is at most',
    after: 'is after',
    before: 'is before',
    contains: 'contains',
    period: 'is in the period',
  },
  /** The periods a date's filter can name, counted from the day the list is looked at. */
  periods: {
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This week',
    lastWeek: 'Last week',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    thisYear: 'This year',
    last7Days: 'The last 7 days',
    last30Days: 'The last 30 days',
    overdue: 'Overdue: before today',
  },
  // ---- a column
  orderBy: 'Order the list by it',
  notOrdered: 'No',
  ascendingShort: 'A to Z, low to high',
  descendingShort: 'Z to A, high to low',
  columnAbout: (label: string, stored: string) => `${label}: ${stored}, from the model. A column shows the field under its own name.`,
  // ---- a column's cells
  cellWidget: 'Drawn as',
  cellWidgets: {
    words: 'Its words',
    dot: 'A state’s dot, then its words',
    badge: 'A pill in its colour',
    tags: 'Chips, one for each',
    image: 'A small picture',
    avatar: 'A round picture, or initials',
    progressbar: 'A bar, and its per cent',
    phone: 'A phone number',
  },
  pillColour: (option: string) => `Pill colour for ${option}`,
  phoneLinks: 'Call and WhatsApp links',
  mayHide: 'People may hide it',
  mayHideChoices: { no: 'No', show: 'Yes, shown at first', hide: 'Yes, hidden at first' },
  mayHideHint: 'With any column people may hide, the header has a Columns menu: people show, hide and order the columns there, and drag a header’s edge to size its column — kept for each of them, Reset putting back yours.',
  dotColour: (option: string) => `Dot colour for ${option}`,
  leadFrom: 'An icon before the words, by',
  noLead: 'None',
  leadIcon: (option: string) => `Icon for ${option}`,
  leadColour: (option: string) => `Icon colour for ${option}`,
  cellWidth: 'Width',
  cellWidthUnit: 'Width in',
  cellWidthUnits: { characters: 'characters', pixels: 'pixels' },
  cellWidthHint: 'In characters of its words, or in pixels for the whole column. Words longer than it end in “…”; a header cut short shows its whole name when pointed at. Empty: as wide as its values. Every column with a width: the table keeps them, and scrolls sideways when wider than the page.',
  cellSide: 'Its words keep to',
  cellSides: { auto: 'As its kind does', start: 'The start', end: 'The end' },
  cellRule: 'Its header’s rule',
  cellRules: { end: 'At its end', none: 'None', in: 'Set in from its end' },
  cellRuleIn: 'Pixels in from its end',
  cellTotal: 'Total',
  cellTotals: { none: 'None', sum: 'The sum', avg: 'The average', min: 'The lowest', max: 'The highest', count: 'How many hold one' },
  cellTotalHint: 'Under the list, on each Group By heading and for the rows ticked. The app’s data source works it out (its totals): with none, no total shows. An amount is added up in one currency only.',
  cellRuleHint: 'The short line that parts it from the next column, where the theme draws one (Fluent’s). None, as a tracker draws a column people cannot size; set in, before the room a tracker keeps at a title’s end for a row’s tools.',
  // ---- a button for the rows chosen
  look: 'Look',
  actionHint: 'The name the app receives with the rows chosen; the app decides what it does.',
  // ---- the list's rows: their tones, bold and buttons
  rows: {
    title: 'Rows',
    toneHint: 'Each row’s words in a colour while a condition on its record holds: the first that holds. A card takes it on its edge.',
    buttons: 'Buttons on each row',
    addButton: 'Add a button on each row',
    buttonsHint: 'A press runs the button with its row’s record alone; the app is given its id. Hidden on a row while its condition holds there.',
    says: 'Says',
    toneSays: 'What this tone means',
    boldSays: 'What bold means',
    saysPlaceholder: 'Overdue',
    boldPlaceholder: 'Your own contacts',
    saysHint: 'Words for a key over the rows — each tone’s swatch, bold’s sample — read by a screen reader too. Without words, no key.',
  },
  // ---- the list's cards and gallery
  cards: {
    title: 'Cards',
    onPhone: 'Each record as a card on a phone',
    gallery: 'A gallery of cards, on any width',
    opens: 'Open on the gallery',
    size: 'Card size',
    sizes: { small: 'Small', medium: 'Medium', large: 'Large' },
    heading: 'Title',
    under: 'Under the title',
    picture: 'Picture',
    status: 'Status pill',
    fromColumns: 'As the columns say',
    nothing: 'Nothing',
    note: 'A card keeps what its row has: its box to tick, its press, its buttons, its tone; Group By headings and totals stay. Lines unset: the next three columns.',
  },
  // ---- the list's calendar
  calendar: {
    title: 'Calendar',
    on: 'Show the records on a calendar too',
    noDate: 'A calendar needs a date, or a date and time, for when each record starts.',
    start: 'Starts',
    end: 'Ends',
    noEnd: 'Nothing: an hour each, or a day',
    words: 'Words on each record',
    colour: 'Colour by',
    resource: 'A column for each',
    nothing: 'Nothing',
    views: 'Views',
    viewNames: { day: 'Day', week: 'Week', month: 'Month', agenda: 'Agenda' },
    oneView: 'A calendar shows one view at least.',
    first: 'Shows first',
    opens: 'The list opens on',
    opensChoices: { list: 'Its table', calendar: 'Its calendar' },
    firstDay: 'The week starts on',
    languageDay: 'The language’s own day',
    days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    from: 'From the hour',
    to: 'To the hour',
    slot: 'A slot holds',
    slots: { 15: '15 minutes', 30: '30 minutes', 60: 'An hour' } as Record<15 | 30 | 60, string>,
    note: 'The app shows the calendar and opens the records made on it. A record outside the hours stretches them.',
  },
  // ---- rows a person works in
  work: {
    title: 'Rows people work in',
    opensTo: 'A row opens to',
    opensToHint: 'Its arrow before the name shows them under the row, a page at a time; the row’s press still opens the record.',
    opensToNothing: 'Nothing: a press opens the record',
    opensToRecords: 'The records pointing at it (in the JSON)',
    rankedBy: 'Rows ranked by hand, by',
    rankedByHint: 'A grip at each row carries it to another place, Alt with an arrow from the keys; the list opens in that order.',
    rankedByNothing: 'Nothing: the list keeps its order',
    endless: 'Rows load as the list is scrolled',
    endlessHint: 'In place of its pages, for a phone’s feed or a log: its count stays, “Show 40 more” at the end for the keys.',
    loadChildren: 'Children load as a parent opens',
    loadChildrenHint: 'For a tree too big for a page, a chart of accounts: its pages are the rows with no parent, each parent’s rows asked as it opens.',
  },
  // ---- the list's timeline
  gantt: {
    title: 'Timeline',
    on: 'Show the records on a timeline too',
    noDate: 'A timeline needs a date, or a date and time, for when each record starts.',
    start: 'Starts',
    end: 'Ends',
    duration: 'Lasts (days)',
    noEnd: 'Nothing: a day each',
    words: 'Words on each bar',
    group: 'Grouped by',
    colour: 'Colour by',
    dependsOn: 'Waits for',
    noWaits: 'A timeline joins records that wait for others by a link to its own records, of many.',
    nothing: 'Nothing',
    zooms: 'Scales',
    zoomNames: { day: 'Days', week: 'Weeks', month: 'Months' },
    oneZoom: 'A timeline shows one scale at least.',
    first: 'Shows first',
    opens: 'The list opens on',
    opensChoices: { list: 'Its table', gantt: 'Its timeline' },
    note: 'A bar runs from its start to its end, or for its days. Moving one writes its start and its end (or its days) through the app’s save, with Undo.',
  },
};
